import React from 'react';
import { IRecommInsightDetail } from "../../../models/InsightsPlatformModels";
import { useState, useRef, useEffect } from "react";
import ReactMarkdown from 'react-markdown';

export interface IInsightDetailsTop10TableProps {
    data: IRecommInsightDetail;
}

const InsightDetailsTop10Table: React.FC<IInsightDetailsTop10TableProps> = (props) => {
    const headers = props.data.listDetail.length > 0 ? Object.keys(props.data.listDetail[0]) : [];
    const [columnWidths, setColumnWidths] = useState<number[]>([]); // Dynamically calculated widths
    const resizingColumnIndex = useRef<number | null>(null);
    const startX = useRef<number | null>(null);

    // Function to calculate the width of text
    const calculateTextWidth = (text: string, font = "16px Arial") => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (context) {
            context.font = font;
            return context.measureText(text).width + 20;
        }
        return 150; // Default width if calculation fails
    };

    // Function to calculate initial column widths
    const calculateColumnWidths = () => {
        const widths = headers.map((header) => {
            // Calculate the width of the header
            let maxWidth = calculateTextWidth(header);
    
            // Calculate the maximum width of the content in this column
            props.data.listDetail.forEach((row) => {
                const cellContent = row[header] || "";
                const cellWidth = calculateTextWidth(cellContent.toString());
                maxWidth = Math.max(maxWidth, cellWidth);
            });

            // If the calculated width exceeds 400px, set it to 1/2 of the width
            if (maxWidth > 400) {
                if (maxWidth > 800) {
                    maxWidth = maxWidth / 5;
                }
                else
                {
                    maxWidth = maxWidth / 2;
                }               
            }
    
            return Math.max(maxWidth, 50); // Ensure a minimum width of 50px
        });
    
        setColumnWidths(widths);
    };

    useEffect(() => {
        calculateColumnWidths();
    }, [props.data]);

    const handleMouseDown = (index: number, event: React.MouseEvent) => {
        resizingColumnIndex.current = index;
        startX.current = event.clientX; // Record the starting X position
    };

    const handleMouseMove = (event: MouseEvent) => {
        if (resizingColumnIndex.current !== null && startX.current !== null) {
            const deltaX = event.clientX - startX.current; // Calculate the horizontal movement
            const newWidths = [...columnWidths];
            const currentWidth = newWidths[resizingColumnIndex.current];

            // Adjust the current column width
            if (currentWidth + deltaX > 50) { // Minimum column width of 50px
                newWidths[resizingColumnIndex.current] = currentWidth + deltaX;
                setColumnWidths(newWidths);
                startX.current = event.clientX; // Update the starting X position
            }
        }
    };

    const handleMouseUp = () => {
        resizingColumnIndex.current = null;
        startX.current = null;
    };

    useEffect(() => {
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [columnWidths]);

    return ( props.data.listDetail.length > 0 ?(
        <div style={{ marginTop: 20, marginBottom: 20 }}>
            
            <div style={{marginBottom:20, fontSize:20, fontWeight:600}}>{props.data.title}</div>

            <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 500, marginTop: 10 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <colgroup>
                        {columnWidths.map((width, index) => (
                            <col key={index} style={{ width: `${width}px` }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {headers.map((header, index) => (
                                <th
                                    key={index}
                                    style={{
                                        border: "1px solid #ddd",
                                        padding: "8px",
                                        textAlign: "left",
                                        fontWeight: 600,
                                        position: "relative",
                                        wordBreak: "break-word", // Allow header text to wrap
                                        // whiteSpace: "normal", // (optional, default is normal)
                                    }}
                                >
                                    {header}
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            right: 0,
                                            width: "5px",
                                            height: "100%",
                                            cursor: "col-resize",
                                            zIndex: 1,
                                        }}
                                        onMouseDown={(e) => handleMouseDown(index, e)}
                                    />
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {props.data.listDetail.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {headers.map((header, colIndex) => (
                                    <td
                                        key={colIndex}
                                        style={{
                                            border: "1px solid #ddd",
                                            padding: "8px",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        <ReactMarkdown>
                                            {row[header]}
                                        </ReactMarkdown>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {props.data.externalLinks && props.data.externalLinks.map((link, index) => (
                <div key={index} style={{ wordBreak: "break-all", marginTop: "10px" }}>
                    <a
                        href={link.address}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#3477EA", textDecoration: "none" }}
                    >
                        {link.displayText}
                    </a>
                </div>
            ))}
        </div>) : (<></>)
    );
};

export default InsightDetailsTop10Table;